
import { supabase } from './supabase/client';

// ── Typen ─────────────────────────────────────────────────────────────────────

export type EntityType =
  | 'objekt' | 'mieter' | 'eigentuemer' | 'kontakt' | 'ticket'
  | 'rechnung' | 'dokument' | 'vertrag' | 'zaehler' | 'termin'
  | 'einheit' | 'notiz' | 'profile';

export type LinkType =
  | 'zustaendigkeit' | 'eigentum' | 'mietverhaeltnis' | 'kontakt'
  | 'zuordnung' | 'bezug';

export interface EntityLink {
  id: string;
  source_type: EntityType;
  source_id: string;
  target_type: EntityType;
  target_id: string;
  link_type: LinkType;
  rolle: string | null;
  ist_primaer: boolean;
  metadata: Record<string, any>;
  created_at: string;
}

// ── Abfragen ──────────────────────────────────────────────────────────────────

/** Alle Links VON einer bestimmten Entität */
export async function getLinksFrom(
  sourceType: EntityType,
  sourceId: string,
  options?: { targetType?: EntityType; linkType?: LinkType }
) {
  let query = supabase
    .from('entity_links')
    .select('*')
    .eq('source_type', sourceType)
    .eq('source_id', sourceId);

  if (options?.targetType) query = query.eq('target_type', options.targetType);
  if (options?.linkType) query = query.eq('link_type', options.linkType);

  return query.order('created_at', { ascending: false });
}

/** Alle Links ZU einer bestimmten Entität */
export async function getLinksTo(
  targetType: EntityType,
  targetId: string,
  options?: { sourceType?: EntityType; linkType?: LinkType }
) {
  let query = supabase
    .from('entity_links')
    .select('*')
    .eq('target_type', targetType)
    .eq('target_id', targetId);

  if (options?.sourceType) query = query.eq('source_type', options.sourceType);
  if (options?.linkType) query = query.eq('link_type', options.linkType);

  return query.order('created_at', { ascending: false });
}

/** Bidirektional: Alle Links, an denen eine Entität beteiligt ist */
export async function getAllLinks(entityType: EntityType, entityId: string) {
  const [fromRes, toRes] = await Promise.all([
    getLinksFrom(entityType, entityId),
    getLinksTo(entityType, entityId),
  ]);
  return {
    outgoing: fromRes.data || [],
    incoming: toRes.data || [],
    all: [...(fromRes.data || []), ...(toRes.data || [])],
  };
}

// ── Schreiben ─────────────────────────────────────────────────────────────────

/** Link erstellen */
export async function createLink(params: {
  sourceType: EntityType;
  sourceId: string;
  targetType: EntityType;
  targetId: string;
  linkType: LinkType;
  rolle?: string;
  istPrimaer?: boolean;
  metadata?: Record<string, any>;
}) {
  return supabase.from('entity_links').upsert({
    source_type: params.sourceType,
    source_id: params.sourceId,
    target_type: params.targetType,
    target_id: params.targetId,
    link_type: params.linkType,
    rolle: params.rolle || null,
    ist_primaer: params.istPrimaer || false,
    metadata: params.metadata || {},
  }, { onConflict: 'source_type,source_id,target_type,target_id,link_type' });
}

/** Link entfernen per Link-ID */
export async function removeLink(linkId: string) {
  return supabase.from('entity_links').delete().eq('id', linkId);
}

/** Link entfernen per Source+Target */
export async function removeLinkBetween(
  sourceType: EntityType, sourceId: string,
  targetType: EntityType, targetId: string,
  linkType: LinkType
) {
  return supabase.from('entity_links').delete()
    .eq('source_type', sourceType).eq('source_id', sourceId)
    .eq('target_type', targetType).eq('target_id', targetId)
    .eq('link_type', linkType);
}

/** Alle Links eines Typs zwischen Source und Target ersetzen */
export async function replaceLinks(params: {
  sourceType: EntityType;
  sourceId: string;
  targetType: EntityType;
  linkType: LinkType;
  newTargetIds: string[];
  rolle?: string;
}) {
  // Alte löschen
  await supabase.from('entity_links').delete()
    .eq('source_type', params.sourceType)
    .eq('source_id', params.sourceId)
    .eq('target_type', params.targetType)
    .eq('link_type', params.linkType);

  // Neue einfügen
  if (params.newTargetIds.length > 0) {
    return supabase.from('entity_links').insert(
      params.newTargetIds.map(targetId => ({
        source_type: params.sourceType,
        source_id: params.sourceId,
        target_type: params.targetType,
        target_id: targetId,
        link_type: params.linkType,
        rolle: params.rolle || null,
      }))
    );
  }
}

/** Einzelnen Link-Target ersetzen (z.B. Zuständigkeit ändern) */
export async function replaceSingleLink(params: {
  sourceType: EntityType;
  sourceId: string;
  targetType: EntityType;
  linkType: LinkType;
  newTargetId: string | null;
}) {
  // Alten löschen
  await supabase.from('entity_links').delete()
    .eq('source_type', params.sourceType)
    .eq('source_id', params.sourceId)
    .eq('target_type', params.targetType)
    .eq('link_type', params.linkType);

  // Neuen setzen (wenn nicht null)
  if (params.newTargetId) {
    return createLink({
      sourceType: params.sourceType,
      sourceId: params.sourceId,
      targetType: params.targetType,
      targetId: params.newTargetId,
      linkType: params.linkType,
    });
  }
}
